import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { ClientBoardResource } from "api/resource/client-board";
import { takeOffFeatured } from "component/shared/mls-id-dialogs";
import { Switch } from "component/shared/switch";
import { AgentMlsIds } from "model/agent-mls-ids";
import { addMlsIdClientBoard, updateMlsIdClientBoard } from "redux/slice/agent-mls-ids";
import { useAppThunkDispatch } from "redux/store";
import { tableKind } from "./featured-table";
import { FeaturedAgentTable, FeaturedView } from "./agent-table";
import { RowAction } from "./agent-table/rows";
import { FeaturedPlainTable } from "./plain-table";
import { PlainEditDialog, PlainValues } from "./plain-edit-dialog";
import { PlainRemoveDialog } from "./plain-remove-dialog";
import { styles } from "./style";

export type { RowAction } from "./agent-table/rows";

interface Props extends WithStyles<typeof styles> {
	agentMlsIds: AgentMlsIds;
	// An agent row's pencil or Fix icon: the Edit/Set modal or one of the fix modals.
	onAction: (action: RowAction) => void;
	// After an Office MLS ID is added, edited or removed, so the page reloads the MLS IDs.
	onChanged: () => void;
}

// The MLS IDs behind Featured Listings: the agents' table for an office-type account, otherwise the plain table of client-board rows.
const Component: React.FC<Props> = ({ agentMlsIds, onAction, onChanged, classes = {} }) => {
	const dispatch = useAppThunkDispatch();
	const kind = tableKind(agentMlsIds);
	const office = kind === "office";
	const boards = agentMlsIds.boards;
	const [view, setView] = useState<FeaturedView>("table");
	const [featuredOnly, setFeaturedOnly] = useState(false);
	const [editing, setEditing] = useState<{ row: ClientBoardResource | null } | null>(null);
	const [removing, setRemoving] = useState<ClientBoardResource | null>(null);

	const savePlain = async (row: ClientBoardResource | null, boardId: number, values: PlainValues) => {
		// An office account's rows have no Backup Office MLS ID; leave whatever the row holds.
		const officeId = office ? (row ? row.officeId : "") : values.alternateId;
		if (row) {
			// strict: the service refuses a duplicate on the board, or a code already on Featured.
			await dispatch(updateMlsIdClientBoard({ clientBoard: { ...row, agentId: values.mlsId, officeId }, strict: true })).unwrap();
		} else {
			const boardName = boards.find((b) => b.boardId === boardId)?.name ?? "";
			// The server assigns the id; the POST ignores this placeholder.
			await dispatch(addMlsIdClientBoard({ clientBoard: { id: 0, boardId, boardName, agentId: values.mlsId, officeId }, strict: true })).unwrap();
		}
		onChanged();
	};
	const removePlain = async (row: ClientBoardResource) => {
		await dispatch(takeOffFeatured(row)).unwrap();
		onChanged();
	};

	return (
		<>
			<div className={classes.bar}>
				<Mui.Alert className={classes.source} role="status" icon={<Icons.Database size={16} />}>
					Listings are sourced from <b>{office ? "Office MLS IDs" : "Agent MLS IDs"}</b>.
				</Mui.Alert>
				{kind !== "agents" ? (
					<Mui.Button variant="contained" color="primary" startIcon={<Icons.Plus size={18} />} onClick={() => setEditing({ row: null })}>
						Add MLS ID
					</Mui.Button>
				) : (
					<div className={classes.tools}>
						<Switch checked={featuredOnly} onChange={setFeaturedOnly} label="On Featured Listings Only" labelBold={false} />
						<Mui.ToggleButtonGroup
							exclusive
							className={classes.segmented}
							value={view}
							onChange={(event, next: FeaturedView | null) => next && setView(next)}
							aria-label="View"
						>
							<Mui.ToggleButton value="board">By Board</Mui.ToggleButton>
							<Mui.ToggleButton value="table">Table</Mui.ToggleButton>
						</Mui.ToggleButtonGroup>
					</div>
				)}
			</div>
			{kind === "agents" ? (
				<FeaturedAgentTable agentMlsIds={agentMlsIds} view={view} featuredOnly={featuredOnly} onAction={onAction} />
			) : (
				<>
					<FeaturedPlainTable agentMlsIds={agentMlsIds} kind={kind} onEdit={(row) => setEditing({ row })} onRemove={setRemoving} />
					<PlainEditDialog
						open={!!editing}
						kind={kind}
						row={editing ? editing.row : null}
						boards={boards}
						onSave={savePlain}
						onClose={() => setEditing(null)}
					/>
					<PlainRemoveDialog
						row={removing}
						boardName={removing ? boards.find((b) => b.boardId === removing.boardId)?.name ?? removing.boardName : ""}
						onRemove={removePlain}
						onClose={() => setRemoving(null)}
					/>
				</>
			)}
		</>
	);
};

export const FeaturedMlsIds = withStyles(Component, styles);
